import { useEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { Copy, GripVertical, X as XIcon } from 'lucide-react';
import {
  BLOCK_REGISTRY,
  BLOCK_TYPES,
  renderBlockOnly,
  themeStyleVars,
  type Block,
  type BlockDefinition,
  type BlockType,
  type RenderContext,
} from '@poof/templates';
import { ApiError, pages as pagesApi, upload as uploadApi, type PageDetail, type PageMeta, type PageTheme } from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Dialog } from '@/components/Dialog';

// ---------------------------------------------------------------------------
// Drag-and-drop drop "intent" — what dragData should be at drop time.
//
//   palette:<type>   user dragging from the palette to insert a new block
//   block:<id>       user dragging an existing block to reorder
//
// We use dataTransfer.setData('text/plain', ...) with that prefix because
// it's the cross-browser-compatible MIME type for native HTML5 DnD.
// ---------------------------------------------------------------------------
type DragIntent =
  | { kind: 'palette'; type: BlockType }
  | { kind: 'block'; id: string };

function parseDragIntent(data: string): DragIntent | null {
  if (data.startsWith('palette:')) {
    const t = data.slice('palette:'.length) as BlockType;
    if (BLOCK_TYPES.includes(t)) return { kind: 'palette', type: t };
  } else if (data.startsWith('block:')) {
    return { kind: 'block', id: data.slice('block:'.length) };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Viewport widths for the canvas frame. Mobile is the default because that's
// where 95% of bio-link traffic lives.
// ---------------------------------------------------------------------------
type Viewport = 'mobile' | 'tablet' | 'desktop';
const VIEWPORT_WIDTHS: Record<Viewport, number> = {
  mobile: 380,
  tablet: 720,
  desktop: 1080,
};

const FONT_OPTIONS: Array<{ value: PageTheme['fontFamily']; label: string }> = [
  { value: 'sans', label: 'Sans' },
  { value: 'sf-pro', label: 'SF Pro (Apple)' },
  { value: 'serif', label: 'Serif' },
  { value: 'mono', label: 'Mono' },
];

const RADIUS_OPTIONS: Array<{ value: PageTheme['radius']; label: string }> = [
  { value: 'sharp', label: 'Sharp' },
  { value: 'soft', label: 'Soft' },
  { value: 'pill', label: 'Pill' },
];

// ---------------------------------------------------------------------------
// Style Presets — bundled looks that swap multiple theme tokens in one tap.
//
// Why: customers were spending 90% of their theme-tab time picking
// primary/background/text colors that work TOGETHER. Most don't have a
// designer's eye for hex pairs. A preset row at the top of the Theme tab
// jumps them to a polished baseline in one click; the per-token controls
// underneath are still there for the customer who wants to fine-tune.
//
// Each preset bundles primary + background + text + font + radius —
// hero image + favicon are deliberately NOT touched (those are customer
// content, not "look"). Picking a preset over an existing custom theme
// is destructive on those five tokens — we'd warn if it felt risky, but
// customers can undo via the per-token controls right below.
//
// Color format matches the rest of the codebase: hex without `#` prefix
// (themeStyleVars + DEFAULT_THEME use this shape).
// ---------------------------------------------------------------------------

type StylePreset = {
  id: string;
  label: string;
  description: string;
  theme: Pick<PageTheme, 'primaryColor' | 'backgroundColor' | 'textColor' | 'fontFamily' | 'radius'>;
};

const STYLE_PRESETS: StylePreset[] = [
  {
    id: 'indigo',
    label: 'Indigo',
    description: 'The Poof default — clean, neutral, works for almost anything.',
    theme: { primaryColor: '4f46e5', backgroundColor: 'ffffff', textColor: '0f172a', fontFamily: 'sans', radius: 'soft' },
  },
  {
    id: 'mono',
    label: 'Mono',
    description: 'Stark black on white. Looks the most "designed by a human."',
    theme: { primaryColor: '0f172a', backgroundColor: 'ffffff', textColor: '0f172a', fontFamily: 'sans', radius: 'sharp' },
  },
  {
    id: 'stealth',
    label: 'Stealth',
    description: 'Dark mode with a violet accent. Strong on phones in the evening.',
    theme: { primaryColor: '7c4dff', backgroundColor: '131313', textColor: 'e5e2e1', fontFamily: 'sans', radius: 'soft' },
  },
  {
    id: 'pop',
    label: 'Pop',
    description: 'Hot pink on white, pill buttons. Reads as energetic + Gen-Z.',
    theme: { primaryColor: 'ec4899', backgroundColor: 'ffffff', textColor: '0f172a', fontFamily: 'sans', radius: 'pill' },
  },
  {
    id: 'neon',
    label: 'Neon',
    description: 'Lime on black, monospace. Tech / underground / gaming.',
    theme: { primaryColor: 'a3e635', backgroundColor: '0a0a0a', textColor: 'fafafa', fontFamily: 'mono', radius: 'pill' },
  },
  {
    id: 'editorial',
    label: 'Editorial',
    description: 'Cream + brown, serif type. Bookish, considered, premium.',
    theme: { primaryColor: '92400e', backgroundColor: 'fef7e7', textColor: '1c1917', fontFamily: 'serif', radius: 'sharp' },
  },
];

/** Returns the preset id whose theme tokens exactly match the current
 *  page theme, or null. Used to highlight the active chip. */
function matchPreset(theme: PageTheme): string | null {
  for (const p of STYLE_PRESETS) {
    if (
      p.theme.primaryColor === theme.primaryColor &&
      p.theme.backgroundColor === theme.backgroundColor &&
      p.theme.textColor === theme.textColor &&
      p.theme.fontFamily === theme.fontFamily &&
      p.theme.radius === theme.radius
    ) {
      return p.id;
    }
  }
  return null;
}

export default function PageEditor() {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState<PageDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  /**
   * Snapshot of the page we sent to the API on the most recent save
   * attempt. Used to detect "the user typed during the in-flight save"
   * so we don't unconditionally clear `dirty` and silently lose those
   * edits. See `save()` for the compare-on-completion logic.
   */
  const savedSnapshotRef = useRef<PageDetail | null>(null);
  const [viewport, setViewport] = useState<Viewport>('mobile');
  const [rightTab, setRightTab] = useState<'inspector' | 'theme'>('inspector');
  /**
   * In-memory clipboard for block-level copy/paste. Cleared only when
   * the editor unmounts. We don't use the system clipboard for this —
   * complete Block JSON would be useless if pasted into another app,
   * and Clipboard API needs user permission + secure context. Works
   * great in-app, which is the only use case.
   */
  const [clipboard, setClipboard] = useState<Block | null>(null);

  /**
   * Wrap setSelectedBlockId so that selecting a block ALSO flips the
   * right pane back to the Inspector tab. Without this, a user who'd
   * opened the Theme tab and then clicked a block would see no visible
   * change — the inspector would be hidden behind the Theme tab. This
   * fixes the "I clicked the block and nothing happened" perception.
   */
  function selectBlock(id: string | null) {
    setSelectedBlockId(id);
    if (id !== null) setRightTab('inspector');
  }

  useEffect(() => {
    if (!id) return;
    pagesApi
      .get(id)
      .then((p) => {
        setPage(p);
        setSelectedBlockId(p.blocks[0]?.id ?? null);
      })
      .catch((e) => setErr((e as Error).message || 'Failed to load page.'));
  }, [id]);

  // Debounced auto-save when dirty.
  //
  // Depends on `saving` too: if a save is in flight, we don't queue
  // another setTimeout. When the in-flight save finishes and the user
  // has edited during it (so `dirty` stayed true via `save`'s snapshot
  // compare), `saving` flips to false, this effect re-fires, and a
  // fresh 1500ms debounce starts. Without `saving` in deps, a save
  // that took longer than 1500ms could leave the page in a "dirty but
  // never re-saved" state because the prior setTimeout fired during
  // the save and was bailed out of by `if (saving) return`.
  useEffect(() => {
    if (!page || !dirty || saving) return;
    const t = setTimeout(() => {
      void save(page, 'auto');
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, dirty, saving]);

  /**
   * Keyboard shortcuts. Scoped to the editor (window-level listener but
   * SKIPS firing when focus is in an input / textarea / contenteditable
   * — so customers can still type the letter "c" in a heading field
   * without copying a block).
   *
   * Cmd/Ctrl+C   copy selected block
   * Cmd/Ctrl+V   paste clipboard (after selected; else append)
   * Cmd/Ctrl+D   duplicate selected
   * Delete/Backspace   remove selected
   * Escape       deselect
   */
  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      const tag = t.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
      if (t.isContentEditable) return true;
      return false;
    }
    function onKeyDown(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();

      if (mod && k === 'c' && selectedBlockId) {
        e.preventDefault();
        copyBlock(selectedBlockId);
        return;
      }
      if (mod && k === 'v' && clipboard) {
        e.preventDefault();
        pasteBlock();
        return;
      }
      if (mod && k === 'd' && selectedBlockId) {
        // Cmd+D in browsers usually bookmarks the page — preventDefault
        // hijacks it for the in-app duplicate action.
        e.preventDefault();
        duplicateBlock(selectedBlockId);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedBlockId) {
        e.preventDefault();
        removeBlock(selectedBlockId);
        return;
      }
      if (e.key === 'Escape' && selectedBlockId) {
        e.preventDefault();
        setSelectedBlockId(null);
        return;
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedBlockId, clipboard]);

  async function save(p: PageDetail, source: 'auto' | 'manual' | 'publish' = 'manual') {
    if (saving) return;
    setSaving(true);
    // Remember what we're saving so we can detect "user typed during
    // the in-flight save" after the API call resolves. If they did, we
    // leave `dirty` alone so the autosave effect fires another round —
    // otherwise the new edits would be marked saved without ever being
    // persisted (the historical bug behind the "stuttery typing" feel:
    // your second keystroke landed mid-save and never got written).
    savedSnapshotRef.current = p;
    try {
      await pagesApi.update(p.id, {
        name: p.name,
        blocks: p.blocks,
        theme: p.theme,
        meta: p.meta,
        status: p.status,
      });
      setSavedAt(Date.now());
      // Functional setState lets us read the CURRENT page (post-await)
      // without stale closure problems. If it matches the snapshot we
      // sent, we're clean. Otherwise the user edited during the await
      // and we leave dirty=true so the debounce re-fires.
      setPage((current) => {
        if (current === savedSnapshotRef.current) {
          setDirty(false);
        }
        return current;
      });
      if (source === 'publish') {
        // Publish path always renews the page reference — explicit
        // user-initiated state change, no race to worry about.
        setPage({ ...p });
        setDirty(false);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function patch(patcher: (p: PageDetail) => PageDetail) {
    setPage((prev) => (prev ? patcher(prev) : prev));
    setDirty(true);
  }

  /** Append a new block at the end. Triggered by clicking a palette item
   *  (drag-and-drop is the primary path; click is the keyboard fallback). */
  function appendBlock(type: BlockType) {
    insertBlockAt(type, page?.blocks.length ?? 0);
  }

  /**
   * Insert a new block of `type` at `targetIndex` (0..len). Triggered by
   * dropping a palette item onto the canvas.
   */
  function insertBlockAt(type: BlockType, targetIndex: number) {
    const def = BLOCK_REGISTRY[type];
    if (!def) return;
    const newId = crypto.randomUUID();
    patch((p) => {
      const next = p.blocks.slice();
      const i = Math.max(0, Math.min(targetIndex, next.length));
      next.splice(i, 0, { id: newId, type, props: def.defaults() });
      return { ...p, blocks: next };
    });
    selectBlock(newId);
  }

  /**
   * Move an existing block to position `targetIndex`. If the block is
   * already at or adjacent to the target (which would be a no-op after
   * the splice math), we skip the patch so the dirty flag doesn't flip
   * pointlessly.
   */
  function moveBlockTo(blockId: string, targetIndex: number) {
    patch((p) => {
      const from = p.blocks.findIndex((b) => b.id === blockId);
      if (from < 0) return p;
      const next = p.blocks.slice();
      const [moved] = next.splice(from, 1);
      // After removing `from`, indices >= from shift down by 1. If the user
      // dropped past `from`, the target shifts down too.
      const adjusted = targetIndex > from ? targetIndex - 1 : targetIndex;
      const clamped = Math.max(0, Math.min(adjusted, next.length));
      next.splice(clamped, 0, moved!);
      return { ...p, blocks: next };
    });
  }

  /** Generic drop handler used by both palette items + reorder. */
  function handleDrop(intent: DragIntent, targetIndex: number) {
    if (intent.kind === 'palette') {
      insertBlockAt(intent.type, targetIndex);
    } else {
      moveBlockTo(intent.id, targetIndex);
    }
  }

  function removeBlock(blockId: string) {
    patch((p) => ({ ...p, blocks: p.blocks.filter((b) => b.id !== blockId) }));
    if (selectedBlockId === blockId) setSelectedBlockId(null);
  }

  /**
   * Copy the currently-selected block to our in-memory clipboard. Deep
   * clone via JSON round-trip so props arrays/objects don't share refs
   * with the source block when later pasted (prevents the "edit the copy,
   * the original changes too" gotcha).
   */
  function copyBlock(blockId: string) {
    if (!page) return;
    const block = page.blocks.find((b) => b.id === blockId);
    if (!block) return;
    setClipboard(JSON.parse(JSON.stringify(block)) as Block);
  }

  /**
   * Insert a fresh clone of the clipboard block. Inserts immediately after
   * `selectedBlockId` when set, otherwise appends to the end. New block
   * gets a fresh UUID + is auto-selected so the customer can edit it right
   * away.
   */
  function pasteBlock() {
    if (!clipboard) return;
    const fresh: Block = {
      ...(JSON.parse(JSON.stringify(clipboard)) as Block),
      id: crypto.randomUUID(),
    };
    patch((p) => {
      const idx = selectedBlockId ? p.blocks.findIndex((b) => b.id === selectedBlockId) : -1;
      const next = p.blocks.slice();
      if (idx >= 0) next.splice(idx + 1, 0, fresh);
      else next.push(fresh);
      return { ...p, blocks: next };
    });
    selectBlock(fresh.id);
  }

  /** Copy + paste in one shot — the standard "Cmd+D duplicate" affordance. */
  function duplicateBlock(blockId: string) {
    if (!page) return;
    const block = page.blocks.find((b) => b.id === blockId);
    if (!block) return;
    const fresh: Block = {
      ...(JSON.parse(JSON.stringify(block)) as Block),
      id: crypto.randomUUID(),
    };
    patch((p) => {
      const idx = p.blocks.findIndex((b) => b.id === blockId);
      if (idx < 0) return p;
      const next = p.blocks.slice();
      next.splice(idx + 1, 0, fresh);
      return { ...p, blocks: next };
    });
    selectBlock(fresh.id);
  }

  function updateBlockProps(blockId: string, props: Record<string, unknown>) {
    patch((p) => ({
      ...p,
      blocks: p.blocks.map((b) => (b.id === blockId ? { ...b, props } : b)),
    }));
  }

  function updateTheme(patchT: Partial<PageTheme>) {
    patch((p) => ({ ...p, theme: { ...p.theme, ...patchT } }));
  }

  function updateMeta(patchM: Partial<PageMeta>) {
    patch((p) => ({ ...p, meta: { ...p.meta, ...patchM } }));
  }

  async function togglePublish() {
    if (!page) return;
    const next: PageDetail = {
      ...page,
      status: page.status === 'published' ? 'draft' : 'published',
    };
    setPage(next);
    await save(next, 'publish');
  }

  // Theme CSS variables for the canvas wrapper — applied inline (not via a
  // <style> block) so they don't leak into the surrounding dashboard chrome.
  const themeVars = useMemo(() => (page ? themeStyleVars(page.theme) : {}), [page]);
  // Editor preview never emits the click tracker — it'd fire synthetic
  // beacons to /__click against app.poof.bio (which is the wrong origin
  // anyway) every time someone clicked the canvas.
  const editorCtx: RenderContext = useMemo(
    () => ({ showBranding: false, plan: 'free', tracking: false }),
    [],
  );

  if (err) {
    return (
      <DashboardLayout title="Edit page">
        <RouterLink to="/app/pages" className="text-sm text-[var(--color-muted-foreground)] hover:underline">
          ← Back to pages
        </RouterLink>
        <p className="mt-4 text-sm text-[var(--color-destructive)]">{err}</p>
      </DashboardLayout>
    );
  }

  if (!page) {
    return (
      <DashboardLayout title="Edit page">
        <div className="h-9 w-72 animate-pulse rounded-md bg-[var(--color-muted)]" />
        <div className="mt-6 h-96 animate-pulse rounded-xl bg-[var(--color-muted)]" />
      </DashboardLayout>
    );
  }

  const selectedBlock = page.blocks.find((b) => b.id === selectedBlockId) || null;
  const canvasWidth = VIEWPORT_WIDTHS[viewport];

  return (
    <DashboardLayout title={page.name} wide>
      {/* Top bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <RouterLink to="/app/pages" className="text-sm text-[var(--color-muted-foreground)] hover:underline">
          ← Pages
        </RouterLink>
        <input
          value={page.name}
          onChange={(e) => patch((p) => ({ ...p, name: e.target.value }))}
          className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-xl font-semibold tracking-tight outline-none transition hover:border-[var(--color-border)] focus:border-[var(--color-ring)]"
        />
        <ViewportToggle value={viewport} onChange={setViewport} />
        <SaveStatus saving={saving} dirty={dirty} savedAt={savedAt} />
        <button
          onClick={() => save(page, 'manual')}
          disabled={!dirty || saving}
          className="rounded-full border border-[var(--color-border)] px-4 py-2 text-sm transition hover:bg-[var(--color-muted)] disabled:opacity-50"
        >
          Save
        </button>
        <button
          onClick={togglePublish}
          disabled={saving}
          className="rounded-full bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
        >
          {page.status === 'published' ? 'Unpublish' : 'Publish'}
        </button>
      </div>

      {/* Three-pane: palette | canvas | inspector/theme */}
      <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)_320px]">
        {/* LEFT: block palette (drag sources) */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-xl border border-[var(--color-border)] p-3">
            <h3 className="mb-2 px-1 text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
              Add blocks
            </h3>
            <p className="mb-3 px-1 text-[11px] text-[var(--color-muted-foreground)]">
              Drag onto the canvas, or click to append.
            </p>
            <Palette onAppend={appendBlock} />
          </div>
        </aside>

        {/* CENTER: canvas wrapped in a viewport-aware device frame.
            The frame SHAPE — not just the width — changes based on the
            viewport tab: iPhone for mobile, iPad-style for tablet, and a
            browser-window chrome (traffic-light dots + address bar) for
            desktop. Just scaling the same phone frame wider read as
            "stretched phone" which was wrong. */}
        <div className="min-w-0">
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--surface-glass)] p-6 backdrop-blur-xl sm:p-8">
            <DeviceFrame
              viewport={viewport}
              canvasWidth={canvasWidth}
              screenBg={`#${page.theme.backgroundColor}`}
            >
              <Canvas
                page={page}
                themeVars={themeVars}
                ctx={editorCtx}
                selectedBlockId={selectedBlockId}
                onSelect={selectBlock}
                onRemove={removeBlock}
                onDuplicate={duplicateBlock}
                onDrop={handleDrop}
              />
            </DeviceFrame>
            {page.blocks.length === 0 && (
              <p className="mt-4 text-center text-xs text-[var(--color-on-surface-variant)]">
                Drag a block from the palette to start, or click any palette item to append.
              </p>
            )}
          </div>
        </div>

        {/* RIGHT: inspector / theme tabs */}
        <aside className="pb-inspector lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-xl border border-[var(--color-border)]">
            <div className="flex border-b border-[var(--color-border)] text-xs">
              <TabButton active={rightTab === 'inspector'} onClick={() => setRightTab('inspector')}>
                {selectedBlock ? 'Block' : 'Inspector'}
              </TabButton>
              <TabButton active={rightTab === 'theme'} onClick={() => setRightTab('theme')}>
                Theme
              </TabButton>
            </div>
            <div className="p-4">
              {rightTab === 'inspector' ? (
                selectedBlock ? (
                  <Inspector
                    block={selectedBlock}
                    onChange={(props) => updateBlockProps(selectedBlock.id, props)}
                  />
                ) : (
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    Click a block on the canvas to edit it.
                  </p>
                )
              ) : (
                <ThemePanel
                  theme={page.theme}
                  meta={page.meta}
                  onChange={updateTheme}
                  onMetaChange={updateMeta}
                />
              )}
            </div>
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}

// ---------------------------------------------------------------------------
// Three-pane editor pieces — palette, canvas with DnD, viewport toggle, theme
// ---------------------------------------------------------------------------

function ViewportToggle({ value, onChange }: { value: Viewport; onChange: (v: Viewport) => void }) {
  const opts: Array<{ value: Viewport; label: string; title: string }> = [
    { value: 'mobile', label: '📱', title: 'Mobile (380px)' },
    { value: 'tablet', label: '📓', title: 'Tablet (720px)' },
    { value: 'desktop', label: '🖥️', title: 'Desktop (1080px)' },
  ];
  return (
    <div className="flex rounded-full border border-[var(--color-border)] p-0.5 text-xs">
      {opts.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          title={o.title}
          className={`rounded-full px-2 py-1 transition ${
            value === o.value ? 'bg-[var(--color-primary)]/15 text-[var(--color-primary)]' : 'hover:bg-[var(--color-muted)]'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-3 py-2 font-medium transition ${
        active
          ? 'border-b-2 border-[var(--color-primary)] text-[var(--color-primary)]'
          : 'text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/40'
      }`}
    >
      {children}
    </button>
  );
}

function Palette({ onAppend }: { onAppend: (t: BlockType) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {BLOCK_TYPES.map((t) => {
        const def = BLOCK_REGISTRY[t] as BlockDefinition<unknown>;
        return (
          <button
            key={t}
            type="button"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('text/plain', `palette:${t}`);
              e.dataTransfer.effectAllowed = 'copyMove';
            }}
            onClick={() => onAppend(t)}
            className="flex cursor-grab flex-col items-center gap-1 rounded-lg border border-[var(--color-border)] p-2.5 text-center text-[11px] transition hover:border-[var(--color-ring)] hover:bg-[var(--color-muted)]/40 active:cursor-grabbing"
            title={`${def.meta.label} — ${def.meta.description}`}
          >
            <span className="text-lg">{def.meta.icon}</span>
            <span className="truncate">{def.meta.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DeviceFrame — viewport-aware chrome around the canvas.
//
// The viewport tab in the toolbar (mobile / tablet / desktop) now actually
// changes the SHAPE of the device, not just its width:
//
//   mobile   → iPhone-style: rounded-rect bezel, pill-shaped Dynamic Island,
//              side buttons painted as thin slits, rounded inner screen.
//   tablet   → iPad-style: thicker uniform bezel, no notch, larger corner
//              radius, screen is the whole interior (no chin).
//   desktop  → macOS-style browser window: titlebar with traffic-light dots
//              and a centered "url-bar pill" showing the live `go.poof.bio/`
//              + the page slug. The "address bar" sells that the customer
//              is previewing exactly what a desktop visitor will see.
//
// All three render the SAME Canvas child inside — the frame is pure chrome.
// ---------------------------------------------------------------------------

function DeviceFrame({
  viewport,
  canvasWidth,
  screenBg,
  children,
}: {
  viewport: Viewport;
  canvasWidth: number;
  /** Forwarded to PhoneFrame for the "behind the notch" strip so the
   *  preview matches the page's actual background instead of leaving a
   *  white gap. */
  screenBg?: string;
  children: React.ReactNode;
}) {
  if (viewport === 'desktop') return <BrowserFrame width={canvasWidth}>{children}</BrowserFrame>;
  if (viewport === 'tablet') return <TabletFrame width={canvasWidth}>{children}</TabletFrame>;
  return <PhoneFrame width={canvasWidth} screenBg={screenBg}>{children}</PhoneFrame>;
}

/** iPhone-style device shell — proportions tuned to feel right at 380px. */
function PhoneFrame({
  width,
  screenBg = '#ffffff',
  children,
}: {
  width: number;
  /** Background color of the inner "screen" area, including the strip
   *  above the dynamic island. Matches the page's own background so
   *  the preview behaves like real iOS, where the page paints behind
   *  the status-bar / notch area instead of leaving a white gap. */
  screenBg?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative mx-auto" style={{ width: '100%', maxWidth: width + 28 }}>
      <DeviceGlow radius="3rem" />
      <div
        className="relative p-3.5"
        style={{
          background: 'linear-gradient(160deg, #2a2a2a 0%, #131313 60%, #0a0a0a 100%)',
          borderRadius: '3rem',
          boxShadow:
            '0 24px 60px -20px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.04) inset, 0 0 0 4px #0a0a0a inset',
        }}
      >
        {/* Side-button slits — sell the "real device" silhouette. */}
        <div
          aria-hidden
          className="absolute -left-[3px] top-[16%] h-12 w-[3px] rounded-l-sm bg-[#2a2a2a]"
        />
        <div
          aria-hidden
          className="absolute -left-[3px] top-[30%] h-20 w-[3px] rounded-l-sm bg-[#2a2a2a]"
        />
        <div
          aria-hidden
          className="absolute -right-[3px] top-[24%] h-16 w-[3px] rounded-r-sm bg-[#2a2a2a]"
        />
        {/* Inner screen — bg defers to the page's own background so the
            42px "above the island" strip looks like iOS does in real life
            (page paints edge-to-edge; the island floats on top). */}
        <div
          className="relative overflow-hidden"
          style={{ borderRadius: '2.25rem', background: screenBg }}
        >
          {/* Dynamic Island — pill, centred, sits ON the screen */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-2.5 z-20 h-[26px] w-[100px] -translate-x-1/2 rounded-full bg-black"
            style={{ boxShadow: '0 1px 0 rgba(255,255,255,0.04) inset' }}
          />
          <div style={{ paddingTop: 42 }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

/** iPad-style — uniform thick bezel, no notch, generous corner radius. */
function TabletFrame({ width, children }: { width: number; children: React.ReactNode }) {
  return (
    <div className="relative mx-auto" style={{ width: '100%', maxWidth: width + 36 }}>
      <DeviceGlow radius="2.25rem" />
      <div
        className="relative p-5"
        style={{
          background: 'linear-gradient(160deg, #2a2a2a 0%, #131313 60%, #0a0a0a 100%)',
          borderRadius: '2.25rem',
          boxShadow:
            '0 24px 60px -20px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.04) inset, 0 0 0 4px #0a0a0a inset',
        }}
      >
        {/* iPad has a single side-button on the long edge */}
        <div
          aria-hidden
          className="absolute -right-[3px] top-12 h-14 w-[3px] rounded-r-sm bg-[#2a2a2a]"
        />
        <div
          className="relative overflow-hidden bg-white"
          style={{ borderRadius: '1rem' }}
        >
          {/* Front-camera dot, centered at top */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-2 z-20 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-black/40"
          />
          {children}
        </div>
      </div>
    </div>
  );
}

/** macOS-style browser window — titlebar w/ traffic-light dots + URL pill. */
function BrowserFrame({ width, children }: { width: number; children: React.ReactNode }) {
  return (
    <div className="relative mx-auto" style={{ width: '100%', maxWidth: width + 16 }}>
      <DeviceGlow radius="1.25rem" />
      <div
        className="relative overflow-hidden"
        style={{
          background: '#1c1b1b',
          borderRadius: '1rem',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow:
            '0 24px 60px -20px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.04) inset',
        }}
      >
        {/* Titlebar */}
        <div
          className="flex items-center gap-2 border-b px-4 py-3"
          style={{
            background: 'linear-gradient(180deg, #2a2a2a, #1c1b1b)',
            borderBottomColor: 'rgba(255,255,255,0.06)',
          }}
        >
          <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
          <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
          <span className="h-3 w-3 rounded-full bg-[#28c840]" />
          <div
            className="ml-3 flex flex-1 items-center justify-center rounded-md px-3 py-1 font-mono text-[11px] text-[var(--color-on-surface-variant)]"
            style={{ background: 'rgba(255,255,255,0.05)' }}
          >
            <span className="opacity-60">go.poof.bio/</span>
            <span className="text-[var(--color-foreground)]">preview</span>
          </div>
          <div className="w-10" />
        </div>
        {/* Browser content area */}
        <div className="relative bg-white">{children}</div>
      </div>
    </div>
  );
}

/** Soft violet glow underneath any device frame. */
function DeviceGlow({ radius }: { radius: string }) {
  return (
    <div
      aria-hidden
      className="absolute inset-0 -z-10 blur-3xl"
      style={{ background: 'rgba(124, 77, 255, 0.18)', borderRadius: radius }}
    />
  );
}

interface CanvasProps {
  page: PageDetail;
  themeVars: Record<string, string>;
  ctx: RenderContext;
  selectedBlockId: string | null;
  onSelect: (id: string | null) => void;
  onRemove: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDrop: (intent: DragIntent, targetIndex: number) => void;
}

function Canvas({ page, themeVars, ctx, selectedBlockId, onSelect, onRemove, onDuplicate, onDrop }: CanvasProps) {
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);

  // Empty-canvas drop: accept a palette drag to insert the first block.
  function onEmptyDrop(e: ReactDragEvent<HTMLDivElement>) {
    e.preventDefault();
    const intent = parseDragIntent(e.dataTransfer.getData('text/plain'));
    if (intent) onDrop(intent, 0);
    setDragOverIndex(null);
  }

  // End-of-canvas drop zone — drops below all blocks.
  function onEndDragOver(e: ReactDragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(page.blocks.length);
  }
  function onEndDrop(e: ReactDragEvent<HTMLDivElement>) {
    e.preventDefault();
    const intent = parseDragIntent(e.dataTransfer.getData('text/plain'));
    if (intent) onDrop(intent, page.blocks.length);
    setDragOverIndex(null);
  }

  const heroImage = page.theme.backgroundImage || '';
  const heroOverlay = page.theme.backgroundOverlay !== false;

  return (
    <div
      ref={canvasRef}
      className="pb-canvas relative"
      onClick={(e) => {
        // Clicking the empty canvas (not a block) clears selection.
        if (e.target === canvasRef.current) onSelect(null);
      }}
      style={{
        ...themeVars,
        minHeight: 200,
        // `contain` creates a containing block so any block using
        // position:fixed (sticky-cta) anchors to the canvas, not the
        // viewport — keeps the editor preview believable.
        contain: 'paint',
        overflow: 'hidden',
      }}
    >
      {/* Hero background — same rendering as the bouncer's renderPage so
          the editor preview matches the live page pixel-for-pixel. */}
      {heroImage && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '75%',
            maxHeight: 760,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url('${heroImage}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              backgroundRepeat: 'no-repeat',
            }}
          />
          {heroOverlay && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(to bottom, transparent 0%, transparent 30%, var(--bg) 95%)',
              }}
            />
          )}
        </div>
      )}
      {page.blocks.length === 0 ? (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={onEmptyDrop}
          className="flex h-48 flex-col items-center justify-center gap-2 border-2 border-dashed border-[var(--color-border)] text-xs text-[var(--color-muted-foreground)]"
          style={{ margin: 20 }}
        >
          <div className="text-2xl">✨</div>
          Drag a block here to begin
        </div>
      ) : (
        <div className="pb-canvas-inner" style={{ position: 'relative', zIndex: 1, padding: '24px 16px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {page.blocks.map((block, i) => (
            <DraggableBlock
              key={block.id}
              block={block as Block}
              index={i}
              theme={page.theme}
              ctx={ctx}
              selected={block.id === selectedBlockId}
              dragOverIndex={dragOverIndex}
              setDragOverIndex={setDragOverIndex}
              onSelect={() => onSelect(block.id)}
              onRemove={() => onRemove(block.id)}
              onDuplicate={() => onDuplicate(block.id)}
              onDrop={onDrop}
            />
          ))}
          {/* End-of-list drop zone (sliver) so users can drop after the
              last block without hovering its lower half. */}
          <div
            onDragOver={onEndDragOver}
            onDrop={onEndDrop}
            onDragLeave={() => setDragOverIndex(null)}
            className="relative h-6"
          >
            {dragOverIndex === page.blocks.length && (
              <div className="absolute inset-x-0 top-1 h-0.5 rounded-full bg-[var(--color-primary)]" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface DraggableBlockProps {
  block: Block;
  index: number;
  theme: PageDetail['theme'];
  ctx: RenderContext;
  selected: boolean;
  dragOverIndex: number | null;
  setDragOverIndex: (n: number | null) => void;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onDrop: (intent: DragIntent, targetIndex: number) => void;
}

/**
 * A single block rendered inline on the canvas. The visible content comes
 * from `renderBlockOnly(block, theme, ctx)` — the exact same call the
 * bouncer uses. We wrap that HTML in a draggable container so the user
 * can drag it to reorder; we also accept drops on it (palette items =
 * insert, other blocks = reorder). Upper-half drop = insert above,
 * lower-half = insert below.
 */
function DraggableBlock({
  block,
  index,
  theme,
  ctx,
  selected,
  dragOverIndex,
  setDragOverIndex,
  onSelect,
  onRemove,
  onDuplicate,
  onDrop,
}: DraggableBlockProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  /**
   * Block render memoization.
   *
   * Earlier this used `JSON.stringify(block.props)` + `JSON.stringify(theme)`
   * as deps, which forced every block on the canvas to re-serialize its
   * full prop tree on EVERY render — including renders triggered by an
   * unrelated block's keystroke. With 8 blocks that's ~16 stringify
   * calls per keypress, with SocialRow / link-list / link-button
   * carrying multi-field arrays. Result: visible input lag on Heading
   * (every letter), and worse on SocialRow URL edits.
   *
   * Switched to reference deps. `updateBlockProps` deliberately keeps
   * the same `block.props` reference for untouched blocks (only the
   * changed block gets a new object). `updateTheme` returns a new theme
   * object on theme edits, which is the right invalidation. Net result:
   * a keystroke only re-renders ONE block.
   */
  const html = useMemo(
    () => renderBlockOnly(block, theme, ctx),
    [block.id, block.props, theme, ctx],
  );
  const def = BLOCK_REGISTRY[block.type as BlockType];

  // Visual feedback during drag — the dragged block dims to 40% so the
  // user sees what they're moving. Cleared on dragend regardless of
  // success, so a cancelled drag (Escape, drop-outside) restores opacity.
  const [isDragging, setIsDragging] = useState(false);

  function onDragStart(e: ReactDragEvent<HTMLDivElement>) {
    e.dataTransfer.setData('text/plain', `block:${block.id}`);
    e.dataTransfer.effectAllowed = 'move';
    setIsDragging(true);
  }

  function onDragOver(e: ReactDragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Determine drop position by Y coordinate within the element.
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const isUpperHalf = e.clientY < rect.top + rect.height / 2;
    setDragOverIndex(isUpperHalf ? index : index + 1);
  }

  function onDragLeave(e: ReactDragEvent<HTMLDivElement>) {
    // Only clear if leaving the element entirely (not just hovering a child).
    if (!ref.current?.contains(e.relatedTarget as Node)) {
      // Keep state for now — the next dragover or dragend will update or clear.
    }
  }

  function handleDropOnBlock(e: ReactDragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    const intent = parseDragIntent(e.dataTransfer.getData('text/plain'));
    if (!intent) {
      setDragOverIndex(null);
      return;
    }
    const rect = ref.current?.getBoundingClientRect();
    let targetIndex = index;
    if (rect) {
      const isUpperHalf = e.clientY < rect.top + rect.height / 2;
      targetIndex = isUpperHalf ? index : index + 1;
    }
    onDrop(intent, targetIndex);
    setDragOverIndex(null);
  }

  const showIndicatorAbove = dragOverIndex === index;
  const showIndicatorBelow = dragOverIndex === index + 1;

  return (
    <div
      ref={ref}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDropOnBlock}
      onDragEnd={() => {
        setIsDragging(false);
        setDragOverIndex(null);
      }}
      onClick={(e) => {
        // The block's rendered HTML can include <a> tags (link-button,
        // link-list rows, social-row icons). The inner-content wrapper
        // below sets pointer-events: none so those never actually
        // receive the click — but we still preventDefault as a belt-
        // and-braces guard for any edge case (text selection, etc.).
        e.preventDefault();
        e.stopPropagation();
        onSelect();
      }}
      // Note: we DELIBERATELY do NOT preventDefault on mousedown anymore.
      // An earlier version of this file called e.preventDefault() on
      // mousedown whenever the target was an <a> tag — but
      // preventDefault on mousedown CANCELS the native drag gesture,
      // which is why link-button blocks were undraggable. The
      // pointer-events: none on the rendered HTML below means anchor
      // mousedowns never reach the inner <a> at all; they hit THIS div
      // directly and start the drag cleanly.
      className={`pb-edit-block group relative cursor-grab transition active:cursor-grabbing ${
        selected
          ? 'rounded-md outline outline-2 outline-[var(--color-primary)] outline-offset-2'
          : 'rounded-md hover:outline hover:outline-1 hover:outline-[var(--color-primary)]/30 hover:outline-offset-2'
      }`}
      style={{
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      {/* Drag handle — visible on hover. A grip glyph in the top-left
          tells the customer "grab here to move." The whole block is
          still draggable; the handle is purely an affordance hint. */}
      {!selected && (
        <div
          aria-hidden
          className="pointer-events-none absolute -left-7 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-[#0a0a0a]/85 text-white opacity-0 shadow-lg transition group-hover:opacity-100"
        >
          <GripVertical size={14} />
        </div>
      )}

      {/* Drop indicator above */}
      {showIndicatorAbove && (
        <div
          className="pointer-events-none absolute -top-2 left-0 right-0 z-10 h-1 rounded-full bg-[var(--color-primary)]"
          style={{ boxShadow: '0 0 14px var(--color-primary)' }}
        />
      )}

      {/*
        Floating toolbar. The block itself is the click target — clicking
        anywhere in the block opens the inspector. The toolbar is purely
        affordance + the two destructive/duplicating actions that don't
        have a natural place in the inspector.
          - Hover (not selected): a small chip showing the block's icon +
            label, telling the user what they're hovering. No "Edit" CTA;
            the rest of the page is the editor.
          - Selected: small round Duplicate + Remove buttons.
       */}
      {!selected && (
        <div
          className="pointer-events-none absolute right-2 top-2 z-10 flex items-center gap-1.5 rounded-full bg-[#0a0a0a]/85 px-2 py-1 text-[11px] text-white opacity-0 shadow transition group-hover:opacity-100"
        >
          <span className="opacity-70">{def?.meta.icon || '◻'}</span>
          <span className="font-medium">{def?.meta.label || block.type}</span>
        </div>
      )}
      {selected && (
        <div
          className="pointer-events-auto absolute right-1.5 top-1.5 z-10 flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0a0a0a]/85 text-white shadow transition hover:bg-[var(--color-primary)]"
            aria-label="Duplicate block"
            title="Duplicate (Ctrl/Cmd+D)"
          >
            <Copy size={11} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0a0a0a]/85 text-white shadow transition hover:bg-[#ef4444]"
            aria-label="Remove block"
            title="Remove (Delete)"
          >
            <XIcon size={11} />
          </button>
        </div>
      )}

      {/*
        Rendered block HTML.
        ────────────────────
        `pointer-events: none` on this wrapper is critical for drag-and-
        drop to work on link-button (and any block with <a> tags inside).
        Without it, browsers initiate their NATIVE anchor-drag gesture
        when the user mousedowns on the rendered link — which (a) tries
        to copy the URL as a text/plain payload, fighting our own
        `block:<id>` payload, and (b) cancels our parent's drag in some
        browsers. Disabling pointer events on the entire inner tree
        means anchor mousedowns hit the parent div directly, kicking
        off OUR drag cleanly. Click selection still works because the
        parent's onClick fires on the bubble phase from the parent
        itself when events pass through to it.
      */}
      <div
        style={{ pointerEvents: 'none' }}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      {/* Drop indicator below */}
      {showIndicatorBelow && (
        <div
          className="pointer-events-none absolute -bottom-2 left-0 right-0 z-10 h-1 rounded-full bg-[var(--color-primary)]"
          style={{ boxShadow: '0 0 14px var(--color-primary)' }}
        />
      )}
    </div>
  );
}

// Default favicon — the Poof cloud emoji (💨), inline SVG data URL. Same
// thing the renderer falls back to when meta.favicon is empty, kept here
// for the editor's "current default" preview thumbnail so customers can
// SEE what their visitors are seeing in the browser tab right now.
const POOF_DEFAULT_FAVICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ctext y='.9em' font-size='90'%3E%F0%9F%92%A8%3C/text%3E%3C/svg%3E";

/** Theme panel — Hero background promoted to the top since it's the
 *  single most visually impactful control. Colors + font follow. */
function ThemePanel({
  theme,
  meta,
  onChange,
  onMetaChange,
}: {
  theme: PageTheme;
  meta: PageMeta;
  onChange: (patch: Partial<PageTheme>) => void;
  onMetaChange: (patch: Partial<PageMeta>) => void;
}) {
  const bgImage = theme.backgroundImage || '';
  const overlay = theme.backgroundOverlay !== false;
  const favicon = meta.favicon || '';
  const activePresetId = matchPreset(theme);
  return (
    <div className="space-y-5">
      {/* Style presets — one-tap "polished baseline" jumper at the very
          top. Cheapest way to give a customer a non-default look. */}
      <StylePresetRow
        activeId={activePresetId}
        onPick={(p) =>
          onChange({
            primaryColor: p.theme.primaryColor,
            backgroundColor: p.theme.backgroundColor,
            textColor: p.theme.textColor,
            fontFamily: p.theme.fontFamily,
            radius: p.theme.radius,
          })
        }
      />

      {/* Hero background — FIRST in the panel because it's the highest-impact
          single control. Slightly more prominent than the rest with a bg
          highlight + larger label so customers don't miss it. */}
      <div className="rounded-lg border-2 border-[var(--color-primary)]/25 bg-[var(--color-primary)]/5 p-3.5 space-y-3">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold">🌄 Hero background</span>
            {bgImage && (
              <span className="rounded-full bg-[#16a34a]/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#16a34a]">
                ON
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">
            A full-bleed photo at the top of the page that fades into your
            background. Biggest single upgrade — pairs perfectly with glass buttons.
          </p>
          <p className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">
            <span className="font-medium text-[var(--color-foreground)]/80">Recommended:</span>{' '}
            portrait orientation, at least <span className="font-mono">1080×1920</span>{' '}
            (9:16). Square works too. JPG or PNG, under ~2 MB after compression.
          </p>
        </div>
        <ImageUploadField
          label="Image"
          value={bgImage}
          onChange={(v) => onChange({ backgroundImage: v || undefined })}
        />
        {bgImage && (
          <>
            <CheckboxField
              label="Fade hero into background (recommended)"
              checked={overlay}
              onChange={(v) => onChange({ backgroundOverlay: v })}
            />
            <button
              type="button"
              onClick={() => onChange({ backgroundImage: undefined })}
              className="text-[11px] text-[var(--color-destructive)] hover:underline"
            >
              Remove hero image
            </button>
          </>
        )}
      </div>

      <ColorPicker
        label="Primary"
        value={theme.primaryColor}
        onChange={(v) => onChange({ primaryColor: v })}
      />
      <ColorPicker
        label="Background"
        value={theme.backgroundColor}
        onChange={(v) => onChange({ backgroundColor: v })}
      />
      <ColorPicker
        label="Text"
        value={theme.textColor}
        onChange={(v) => onChange({ textColor: v })}
      />
      <SelectField
        label="Font"
        value={theme.fontFamily}
        options={FONT_OPTIONS}
        onChange={(v) => onChange({ fontFamily: v })}
      />
      <SelectField
        label="Radius"
        value={theme.radius}
        options={RADIUS_OPTIONS}
        onChange={(v) => onChange({ radius: v })}
      />

      {/* Public title — opt-in. Shown in the browser tab and to link-
          preview crawlers (Meta, Twitter/X, iMessage, Slack). NEVER
          auto-derived from the internal page name: a decoy page named
          "Safe Decoy" leaking as og:title is what triggered an IG
          restriction on 2026-05-19.

          Default when left empty:
            • Real-lander page → the link's short code (e.g. /camcow
              renders <title>camcow</title>)
            • Decoy page       → empty (no <title> content, og:title
              tag omitted entirely)

          120-char cap matches normalizeMeta in @poof/templates. */}
      <div className="rounded-lg border border-[var(--color-border)] p-3.5 space-y-3">
        <div>
          <div className="text-sm font-semibold">Public title</div>
          <p className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">
            Shown in the visitor&apos;s browser tab and in link-preview cards
            (iMessage, social, etc.). Leave empty and we&apos;ll use the
            link&apos;s short code automatically for real pages — and nothing
            for decoy pages, so they stay anonymous.
          </p>
        </div>
        <TextField
          label="Title (optional)"
          value={meta.title || ''}
          onChange={(v) =>
            onMetaChange({ title: v.trim() ? v : undefined })
          }
          placeholder="e.g. Camila's Latest Drop"
          maxLength={120}
        />
      </div>

      {/* Browser-tab icon — small but high-perceived-quality detail.
          When visitors open this link in mobile Safari / Chrome they
          see the favicon in the tab strip + on their home-screen if
          they "Add to Home Screen". Empty = Poof's cloud (💨), so
          every page ships with SOMETHING; customers upgrade by
          uploading their own square image. */}
      <div className="rounded-lg border border-[var(--color-border)] p-3.5 space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <img
              src={favicon || POOF_DEFAULT_FAVICON}
              alt=""
              className="h-6 w-6 rounded-sm border border-[var(--color-border)] bg-white object-cover"
            />
            <span className="text-sm font-semibold">Browser tab icon</span>
            {!favicon && (
              <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)]">
                Default
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">
            Shown in the browser tab + on the home screen when visitors save
            your link. Using Poof&apos;s cloud (💨) by default — upload your own
            square image to brand it.
          </p>
        </div>
        <ImageUploadField
          label="Custom icon (PNG/SVG, square works best)"
          value={favicon}
          onChange={(v) => onMetaChange({ favicon: v || undefined })}
        />
        {favicon && (
          <button
            type="button"
            onClick={() => onMetaChange({ favicon: undefined })}
            className="text-[11px] text-[var(--color-destructive)] hover:underline"
          >
            Use Poof default
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Style preset row — small chips, each rendering a miniature swatch of
 * the bundled theme so customers can SEE the look before they tap.
 *
 * The active chip (the one whose tokens currently match the page) gets
 * a primary-tinted ring and a checkmark. Tapping a chip applies all five
 * tokens at once — destructive on the per-token controls below, but
 * customers can fine-tune from there.
 */
function StylePresetRow({
  activeId,
  onPick,
}: {
  activeId: string | null;
  onPick: (preset: StylePreset) => void;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] p-3.5 space-y-3">
      <div>
        <div className="text-sm font-semibold">🎨 Style preset</div>
        <p className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">
          Pick a polished baseline. Fine-tune the colors and font below.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {STYLE_PRESETS.map((p) => {
          const active = p.id === activeId;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p)}
              title={p.description}
              className={`group relative flex flex-col gap-1.5 rounded-md border p-2 text-left transition ${
                active
                  ? 'border-[var(--color-primary)] ring-2 ring-[var(--color-primary)]/40'
                  : 'border-[var(--color-border)] hover:border-[var(--color-ring)]'
              }`}
            >
              {/* Miniature swatch — a tiny faux-page that previews the
                  bundled theme. Background = bg, "title" = text, "button"
                  = primary, font + radius applied directly. */}
              <div
                style={{
                  background: `#${p.theme.backgroundColor}`,
                  color: `#${p.theme.textColor}`,
                  fontFamily:
                    p.theme.fontFamily === 'serif'
                      ? 'ui-serif, Georgia, serif'
                      : p.theme.fontFamily === 'mono'
                      ? 'ui-monospace, SFMono-Regular, monospace'
                      : p.theme.fontFamily === 'sf-pro'
                      ? '"SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif'
                      : 'ui-sans-serif, system-ui, sans-serif',
                }}
                className="flex h-14 w-full flex-col items-stretch justify-center gap-1 rounded-sm border border-black/10 px-2"
              >
                <div
                  style={{ fontSize: 9, fontWeight: 600, lineHeight: 1 }}
                >
                  Aa
                </div>
                <div
                  style={{
                    background: `#${p.theme.primaryColor}`,
                    borderRadius:
                      p.theme.radius === 'pill'
                        ? 9999
                        : p.theme.radius === 'sharp'
                        ? 0
                        : 4,
                    height: 8,
                  }}
                />
              </div>
              <div className="text-xs font-medium leading-none">{p.label}</div>
              {active && (
                <span className="absolute right-1.5 top-1.5 rounded-full bg-[var(--color-primary)] px-1 text-[8px] font-bold text-[var(--color-primary-foreground)]">
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SaveStatus({
  saving,
  dirty,
  savedAt,
}: {
  saving: boolean;
  dirty: boolean;
  savedAt: number | null;
}) {
  if (saving) return <span className="text-xs text-[var(--color-muted-foreground)]">Saving…</span>;
  if (dirty)
    return <span className="text-xs text-[var(--color-muted-foreground)]">Unsaved changes</span>;
  if (savedAt)
    return <span className="text-xs text-[#16a34a]">✓ Saved</span>;
  return null;
}

// (BlockSummary + summarize removed — the new drag-and-drop canvas
// renders each block inline so the textual summary in the old vertical
// list is no longer needed.)

/**
 * Inspector — renders a per-block-type form. We hand-wire each type for
 * Phase A; Phase B can generate this from each block's zod schema.
 */
function Inspector({
  block,
  onChange,
}: {
  block: Block;
  onChange: (props: Record<string, unknown>) => void;
}) {
  const def = BLOCK_REGISTRY[block.type];
  return (
    <section className="rounded-xl border border-[var(--color-border)] p-4">
      <h3 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        <span className="text-lg">{def?.meta.icon}</span>
        {def?.meta.label}
      </h3>
      <div className="mt-3 space-y-3">
        {block.type === 'avatar' && <AvatarInspector props={block.props} onChange={onChange} />}
        {block.type === 'heading' && <HeadingInspector props={block.props} onChange={onChange} />}
        {block.type === 'paragraph' && <ParagraphInspector props={block.props} onChange={onChange} />}
        {block.type === 'link-button' && <LinkButtonInspector props={block.props} onChange={onChange} />}
        {block.type === 'social-row' && <SocialRowInspector props={block.props} onChange={onChange} />}
        {block.type === 'image' && <ImageInspector props={block.props} onChange={onChange} />}
        {block.type === 'divider' && <DividerInspector props={block.props} onChange={onChange} />}
        {block.type === 'countdown' && <CountdownInspector props={block.props} onChange={onChange} />}
        {block.type === 'stock-pill' && <StockPillInspector props={block.props} onChange={onChange} />}
        {block.type === 'video-embed' && <VideoEmbedInspector props={block.props} onChange={onChange} />}
        {block.type === 'sticky-cta' && <StickyCtaInspector props={block.props} onChange={onChange} />}
        {block.type === 'link-list' && <LinkListInspector props={block.props} onChange={onChange} />}
        {block.type === 'carousel' && <CarouselInspector props={block.props} onChange={onChange} />}
        {block.type === 'live-status' && <LiveStatusInspector props={block.props} onChange={onChange} />}
        {block.type === 'instagram-bio' && <InstagramBioInspector props={block.props} onChange={onChange} />}
        {block.type === 'spacer' && <SpacerInspector props={block.props} onChange={onChange} />}
      </div>
    </section>
  );
}

type Props = Record<string, unknown>;
type InspectorProps = { props: Props; onChange: (p: Props) => void };

function AvatarInspector({ props, onChange }: InspectorProps) {
  const src = (props.src as string) || '';
  const cropX = typeof props.cropX === 'number' ? props.cropX : 50;
  const cropY = typeof props.cropY === 'number' ? props.cropY : 50;
  const showOnlineDot = !!props.showOnlineDot;
  return (
    <>
      <ImageUploadField
        label="Image URL"
        value={src}
        onChange={(v) =>
          onChange({
            ...props,
            src: v,
            // Reset positioning when the photo changes — the old crop
            // coordinates probably don't make sense on the new image.
            cropX: 50,
            cropY: 50,
          })
        }
      />
      {src && (
        <AvatarCropper
          src={src}
          x={cropX}
          y={cropY}
          onChange={(x, y) => onChange({ ...props, cropX: x, cropY: y })}
          onReset={() => onChange({ ...props, cropX: 50, cropY: 50 })}
        />
      )}
      <TextField
        label="Initials (fallback)"
        value={(props.initials as string) || ''}
        maxLength={3}
        onChange={(v) => onChange({ ...props, initials: v })}
      />
      <SelectField
        label="Size"
        value={(props.size as string) || 'md'}
        options={[
          { value: 'sm', label: 'Small' },
          { value: 'md', label: 'Medium' },
          { value: 'lg', label: 'Large' },
        ]}
        onChange={(v) => onChange({ ...props, size: v })}
      />

      <div className="space-y-2 rounded-md border border-[var(--color-border)] p-3">
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Overlays
        </span>
        <p className="text-[11px] text-[var(--color-muted-foreground)]">
          Want a verified checkmark? Real social apps put it inline next to the name,
          not on the avatar. Turn it on in the <strong>Heading</strong> block, or use
          the <strong>Instagram bio</strong> block for the full IG-style header.
        </p>
        <label className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-[var(--color-muted)]/40">
          <input
            type="checkbox"
            checked={showOnlineDot}
            onChange={(e) => onChange({ ...props, showOnlineDot: e.target.checked })}
            className="mt-0.5 h-4 w-4"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              Online dot
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: 9999,
                  background: '#22c55e',
                  boxShadow: '0 0 0 1.5px #fff, 0 0 0 2px rgba(0,0,0,0.15)',
                }}
              />
            </div>
            <div className="text-xs text-[var(--color-muted-foreground)]">
              Static green dot in the bottom-right (Discord-style &ldquo;active&rdquo;).
            </div>
          </div>
        </label>
      </div>
    </>
  );
}

/**
 * AvatarCropper — Instagram-style pan-to-position UI. The user drags
 * inside the circular preview to choose which part of the uploaded photo
 * fills the avatar circle. State is just two numbers (cropX, cropY in
 * 0–100), which map directly to `object-position: X% Y%` on the rendered
 * <img>. No actual cropping happens — the original image stays intact in
 * R2 and can be re-positioned anytime.
 */
function AvatarCropper({
  src,
  x,
  y,
  onChange,
  onReset,
}: {
  src: string;
  x: number;
  y: number;
  onChange: (x: number, y: number) => void;
  onReset: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!ref.current) return;
    // Capture so movement outside the circle still fires events here.
    try {
      ref.current.setPointerCapture(e.pointerId);
    } catch {
      // some browsers throw on touch capture errors — ignore
    }
    setDragging(true);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragging || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    // Pixel movement → percent shift on object-position. Dragging the
    // image to the right means the user wants to see what's currently
    // *to the left* of the visible window → decrease X.
    const dxPct = (e.movementX / rect.width) * 100;
    const dyPct = (e.movementY / rect.height) * 100;
    const nextX = Math.max(0, Math.min(100, x - dxPct));
    const nextY = Math.max(0, Math.min(100, y - dyPct));
    // Skip the patch if neither axis moved a meaningful amount — avoids
    // burning the dirty flag on micro-jitter from trackpads.
    if (Math.abs(nextX - x) < 0.01 && Math.abs(nextY - y) < 0.01) return;
    onChange(nextX, nextY);
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    setDragging(false);
    if (ref.current) {
      try {
        ref.current.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Position the photo
        </span>
        <button
          type="button"
          onClick={onReset}
          className="text-[11px] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:underline"
        >
          Center
        </button>
      </div>
      <div
        ref={ref}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          width: '100%',
          maxWidth: 220,
          aspectRatio: '1 / 1',
          borderRadius: 9999,
          margin: '0 auto',
          backgroundImage: `url(${src})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: `${x}% ${y}%`,
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          userSelect: 'none',
          boxShadow:
            '0 0 0 2px var(--color-border), 0 12px 32px rgba(0,0,0,0.18)',
          position: 'relative',
        }}
        role="application"
        aria-label="Drag to position the photo inside the circle"
      >
        {/* Subtle "drag to position" hint, fades out while dragging */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingBottom: 14,
            pointerEvents: 'none',
            opacity: dragging ? 0 : 1,
            transition: 'opacity 0.15s ease',
          }}
        >
          <span
            style={{
              fontSize: 11,
              padding: '4px 10px',
              borderRadius: 9999,
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              fontWeight: 500,
              letterSpacing: 0.2,
              textShadow: '0 1px 2px rgba(0,0,0,0.4)',
            }}
          >
            Drag to position
          </span>
        </div>
      </div>
    </div>
  );
}

function HeadingInspector({ props, onChange }: InspectorProps) {
  return (
    <>
      <TextField
        label="Text"
        value={(props.text as string) || ''}
        onChange={(v) => onChange({ ...props, text: v })}
      />
      <SelectField
        label="Level"
        value={String(props.level || 1)}
        options={[
          { value: '1', label: 'H1 (largest)' },
          { value: '2', label: 'H2' },
          { value: '3', label: 'H3' },
        ]}
        onChange={(v) => onChange({ ...props, level: parseInt(v, 10) })}
      />
      <AlignField value={(props.align as string) || 'center'} onChange={(v) => onChange({ ...props, align: v })} />
    </>
  );
}

function SpacerInspector({ props, onChange }: InspectorProps) {
  const height = typeof props.height === 'number' ? props.height : 64;
  return (
    <>
      <p className="text-xs text-[var(--color-muted-foreground)]">
        Invisible vertical space. Use it to push content down so a hero background
        image bleeds through behind it, or just for breathing room.
      </p>
      <div>
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Height
          </span>
          <span className="font-mono text-xs text-[var(--color-muted-foreground)]">
            {height}px
          </span>
        </div>
        <input
          type="range"
          min={4}
          max={500}
          step={4}
          value={height}
          onChange={(e) => onChange({ ...props, height: Number(e.target.value) })}
          className="mt-2 w-full"
        />
        <div className="mt-2 grid grid-cols-5 gap-1.5">
          {[
            { value: 16, label: 'XS' },
            { value: 48, label: 'S' },
            { value: 96, label: 'M' },
            { value: 200, label: 'L' },
            { value: 400, label: 'XL' },
          ].map((p) => (
            <button
              type="button"
              key={p.value}
              onClick={() => onChange({ ...props, height: p.value })}
              className={`rounded-md border px-2 py-1 text-xs transition ${
                height === p.value
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                  : 'border-[var(--color-border)] hover:bg-[var(--color-muted)]/40'
              }`}
              title={`${p.value}px`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-[var(--color-muted-foreground)]">
          Tip: 200–400px works well to expose a hero photo before content starts.
        </p>
      </div>
    </>
  );
}

function ParagraphInspector({ props, onChange }: InspectorProps) {
  return (
    <>
      <TextAreaField
        label="Text"
        value={(props.text as string) || ''}
        onChange={(v) => onChange({ ...props, text: v })}
      />
      <AlignField value={(props.align as string) || 'center'} onChange={(v) => onChange({ ...props, align: v })} />
    </>
  );
}

function LinkButtonInspector({ props, onChange }: InspectorProps) {
  const icon = (props.icon as string) || '';
  const iconBg = (props.iconBg as string) || 'primary';
  return (
    <>
      <TextField
        label="Label"
        value={(props.label as string) || ''}
        onChange={(v) => onChange({ ...props, label: v })}
      />
      <TextField
        label="URL"
        value={(props.url as string) || ''}
        onChange={(v) => onChange({ ...props, url: v })}
        placeholder="https://"
      />
      <SelectField
        label="Style"
        value={(props.variant as string) || 'solid'}
        options={[
          { value: 'solid', label: 'Solid (filled with primary color)' },
          { value: 'outline', label: 'Outline' },
          { value: 'glass', label: 'Glass (frosted, best on hero photo)' },
        ]}
        onChange={(v) => onChange({ ...props, variant: v })}
      />

      {props.variant === 'glass' && (
        <p className="-mt-2 rounded-md border border-[var(--color-primary)]/25 bg-[var(--color-primary)]/5 p-2.5 text-[11px] text-[var(--color-muted-foreground)]">
          💡 <strong className="text-[var(--color-foreground)]">Glass</strong> looks
          frosted-translucent — needs a busy background to read. Open the{' '}
          <strong className="text-[var(--color-foreground)]">Theme</strong> tab on the
          right and add a hero image at the top.
        </p>
      )}

      {/* Icon — optional small emoji shown in a circular container on
          the left. When set, the button switches to the horizontal-card
          layout from DESIGN.md. */}
      <div className="rounded-md border border-[var(--color-border)] p-3 space-y-2">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Left icon (optional)
          </span>
          {icon && (
            <button
              type="button"
              onClick={() => onChange({ ...props, icon: '' })}
              className="text-[11px] text-[var(--color-muted-foreground)] hover:underline"
            >
              Remove
            </button>
          )}
        </div>
        <input
          type="text"
          value={icon}
          maxLength={4}
          onChange={(e) => onChange({ ...props, icon: e.target.value })}
          placeholder="Drop an emoji here — e.g. 🔥 📺 🛍️ ✉️"
          className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
        />
        {icon && (
          <SelectField
            label="Icon background"
            value={iconBg}
            options={[
              { value: 'primary', label: 'Theme primary (default)' },
              { value: 'secondary', label: 'Subtle frosted (matches glass)' },
              { value: 'transparent', label: 'Transparent' },
            ]}
            onChange={(v) => onChange({ ...props, iconBg: v })}
          />
        )}
      </div>

      <CheckboxField
        label="Open in new tab"
        checked={props.newTab !== false}
        onChange={(v) => onChange({ ...props, newTab: v })}
      />
    </>
  );
}

interface SocialItemRaw {
  platform: string;
  /** Legacy single-URL shape (kept for D1 rows saved before the
   *  per-icon rotation feature shipped). */
  url?: string;
  /** New shape — rotation pool for this icon. */
  urls?: string[];
}

/** Pull the URL pool out of an item that may be in either shape. */
function getItemUrls(it: SocialItemRaw): string[] {
  if (Array.isArray(it.urls)) return it.urls;
  if (typeof it.url === 'string') return [it.url];
  return [];
}

/**
 * Best-effort username extraction from a profile URL so the rotation
 * pool list shows `@handle` instead of a wall of identical-looking
 * URLs. Falls back to the host name (or the URL itself) when the
 * platform's URL structure doesn't expose a clean handle.
 *
 * Per-platform conventions vary — TikTok and YouTube put '@' in the
 * path, X / Instagram / Threads / Facebook just use the username
 * directly. LinkedIn and Spotify use a /in/ or /user/ prefix. The
 * mappings below cover the obvious cases; everything else just shows
 * the raw URL trimmed of scheme.
 */
function extractHandle(rawUrl: string, platform: string): string {
  const url = (rawUrl || '').trim();
  if (!url) return '';
  // Special-case mailto: addresses — they don't parse with new URL()
  // on every browser and we want the plain email back anyway.
  if (platform === 'email' || /^mailto:/i.test(url)) {
    return url.replace(/^mailto:/i, '');
  }
  let parsed: URL;
  try {
    parsed = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
  } catch {
    return url;
  }
  const segments = parsed.pathname.split('/').filter((s) => s.length > 0);
  const first = segments[0] || '';
  const second = segments[1] || '';
  const at = (s: string) => '@' + s.replace(/^@/, '');
  switch (platform) {
    case 'x':
    case 'instagram':
    case 'threads':
    case 'facebook':
      return first ? at(first) : parsed.hostname;
    case 'tiktok':
      return first ? at(first) : parsed.hostname;
    case 'youtube':
      if (first.startsWith('@')) return first;
      if (['c', 'user', 'channel'].includes(first) && second) return at(second);
      return first ? at(first) : parsed.hostname;
    case 'linkedin':
      // linkedin.com/in/<handle> or linkedin.com/company/<name>
      if ((first === 'in' || first === 'company') && second) return second;
      return first || parsed.hostname;
    case 'spotify':
      // spotify.com/user/<id> or spotify.com/artist/<id>
      return second || first || parsed.hostname;
    case 'discord':
      return parsed.hostname + parsed.pathname;
    case 'website':
      return parsed.hostname;
    default:
      return first ? at(first) : parsed.hostname;
  }
}

/**
 * Bulk-add modal for a Social row item's rotation pool. The creator
 * pastes a list (one URL per line), we parse + de-dup against the
 * existing pool, and append the new ones in order. Easier than
 * "Add URL" 50 times.
 */
function AddRotatorUrlsDialog({
  open,
  platform,
  existing,
  onClose,
  onAdd,
}: {
  open: boolean;
  platform: string;
  existing: string[];
  onClose: () => void;
  onAdd: (urls: string[]) => void;
}) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (open) setText('');
  }, [open]);

  // Parse the textarea live so the "Add N URLs" button reflects what
  // would actually be added (already de-duped against the pool).
  const parsed = text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const existingSet = new Set(existing.map((u) => u.trim()));
  const fresh = parsed.filter((u, i) => !existingSet.has(u) && parsed.indexOf(u) === i);
  const dupCount = parsed.length - fresh.length;
  const headroom = Math.max(0, 100 - existing.length);
  const willAdd = fresh.slice(0, headroom);
  const overflow = fresh.length - willAdd.length;

  function submit() {
    if (willAdd.length === 0) return;
    onAdd(willAdd);
    onClose();
  }

  if (!open) return null;
  return (
    <Dialog open={open} onClose={onClose} labelledBy="add-urls-title">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-3">
          <h2 id="add-urls-title" className="text-base font-semibold">
            Add URLs to your {platform} rotation
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/40"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="space-y-3 px-5 py-4">
          <p className="text-[12px] text-[var(--color-muted-foreground)]">
            Paste one URL per line. Duplicates of URLs already in the
            pool are skipped automatically.
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              platform === 'x'
                ? 'https://x.com/account-one\nhttps://x.com/account-two\nhttps://x.com/account-three'
                : 'https://…\nhttps://…\nhttps://…'
            }
            rows={12}
            spellCheck={false}
            autoFocus
            className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-2 font-mono text-[12px] leading-relaxed outline-none transition focus:border-[var(--color-ring)]"
          />
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-[11px] text-[var(--color-muted-foreground)]">
            <span>
              {willAdd.length === 0
                ? 'Nothing to add yet'
                : `${willAdd.length} new URL${willAdd.length === 1 ? '' : 's'} will be added`}
              {dupCount > 0 && (
                <span className="ml-2 text-[var(--color-muted-foreground)]/80">
                  · {dupCount} duplicate{dupCount === 1 ? '' : 's'} skipped
                </span>
              )}
            </span>
            {overflow > 0 && (
              <span className="text-[var(--color-destructive)]">
                {overflow} would exceed the 100-URL cap and won't be added
              </span>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[var(--color-border)] px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={willAdd.length === 0}
            className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-50"
          >
            {willAdd.length === 0
              ? 'Add URLs'
              : `Add ${willAdd.length} URL${willAdd.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function SocialRowInspector({ props, onChange }: InspectorProps) {
  const items = (props.items as SocialItemRaw[]) || [];
  const PLATFORMS = [
    'instagram',
    'tiktok',
    'x',
    'youtube',
    'linkedin',
    'spotify',
    'threads',
    'discord',
    'facebook',
    'website',
    'email',
  ];

  // null when no dialog is open; otherwise { itemIndex } identifies which
  // social-row entry's pool we're appending to. Single source of truth so
  // the dialog can read the current pool back from `items` even after
  // the user closes + reopens.
  const [addingTo, setAddingTo] = useState<{ itemIndex: number } | null>(null);

  function writeItems(next: Array<{ platform: string; urls: string[] }>) {
    onChange({ ...props, items: next });
  }

  function updateItemUrls(itemIndex: number, nextUrls: string[]) {
    const next = items.map((x, j) =>
      j === itemIndex
        ? { platform: x.platform, urls: nextUrls }
        : { platform: x.platform, urls: getItemUrls(x) },
    );
    writeItems(next);
  }

  const activePool = addingTo ? getItemUrls(items[addingTo.itemIndex] || { platform: 'instagram' }) : [];
  const activePlatform = addingTo ? items[addingTo.itemIndex]?.platform || 'instagram' : 'instagram';

  return (
    <>
      <AlignField
        value={(props.align as string) || 'center'}
        onChange={(v) => onChange({ ...props, align: v })}
      />
      <div className="space-y-3">
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Profiles
        </span>
        {items.map((it, i) => {
          const urls = getItemUrls(it);
          const multi = urls.length > 1;
          const extras = urls.slice(1);
          // Handle extracted from the PRIMARY URL — shown as a small
          // hint under the input so the creator can verify "@cami"
          // is what visitors will land on without parsing the URL by
          // eye. Empty / unparseable URLs return the raw value, which
          // we suppress so we don't show "https://" as a fake handle.
          const primaryUrl = urls[0] ?? '';
          const primaryHandle = primaryUrl ? extractHandle(primaryUrl, it.platform) : '';
          const showPrimaryHint =
            !!primaryHandle &&
            primaryHandle !== primaryUrl &&
            primaryHandle !== 'https' &&
            primaryHandle.length > 0;
          return (
            <div
              key={i}
              className={`space-y-1.5 rounded-md ${multi ? 'border border-[var(--color-border)] p-2' : ''}`}
            >
              {/* Header row: platform + primary URL + remove-profile.
                  Primary URL stays a free-text input — the rotation
                  pool's "extra" URLs are managed via the modal + chip
                  list below. */}
              <div className="flex gap-2">
                <select
                  value={it.platform}
                  onChange={(e) => {
                    const next = items.map((x, j) => ({
                      platform: j === i ? e.target.value : x.platform,
                      urls: getItemUrls(j === i ? { ...x, platform: e.target.value } : x),
                    }));
                    writeItems(next);
                  }}
                  className="rounded-md border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm"
                >
                  {PLATFORMS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <input
                  value={urls[0] ?? ''}
                  onChange={(e) => {
                    const nextUrls = urls.length === 0 ? [e.target.value] : urls.slice();
                    nextUrls[0] = e.target.value;
                    updateItemUrls(i, nextUrls);
                  }}
                  placeholder="https://…"
                  className="min-w-0 flex-1 rounded-md border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm"
                />
                <button
                  type="button"
                  onClick={() =>
                    writeItems(
                      items
                        .filter((_, j) => j !== i)
                        .map((x) => ({ platform: x.platform, urls: getItemUrls(x) })),
                    )
                  }
                  className="rounded px-2 text-sm text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
                  aria-label="Remove profile"
                  title="Remove this profile"
                >
                  ✕
                </button>
              </div>

              {/* Extracted handle for the primary URL — small hint
                  under the input so the creator can confirm what
                  visitors will land on without parsing the URL in
                  their head. Aligned with the URL input column. */}
              {showPrimaryHint && (
                <div className="pl-[112px] text-[11px] text-[var(--color-muted-foreground)]">
                  → <span className="text-[var(--color-foreground)]/80">{primaryHandle}</span>
                </div>
              )}

              {/* Extra URLs (rotation pool). Each row shows the
                  extracted username so the creator can scan a long
                  list ("@account-23") instead of squinting at 50
                  identical https://x.com/… URLs. Full URL is on the
                  title attribute for hover-confirmation. */}
              {extras.length > 0 && (
                <ul className="ml-[112px] space-y-1">
                  {extras.map((u, k) => {
                    const handle = extractHandle(u, it.platform) || u;
                    return (
                      <li
                        key={k + 1}
                        className="flex items-center justify-between gap-2 rounded-md bg-[var(--surface-glass)] px-2 py-1"
                      >
                        <span
                          className="min-w-0 truncate text-[12px] text-[var(--color-foreground)]"
                          title={u}
                        >
                          {handle}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const nextUrls = urls.filter((_, idx) => idx !== k + 1);
                            updateItemUrls(i, nextUrls);
                          }}
                          className="shrink-0 rounded px-2 text-xs text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
                          aria-label="Remove URL"
                          title={`Remove ${handle} from the rotation pool`}
                        >
                          ✕
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Footer: + Add URL (opens modal) + counter when
                  rotating. */}
              <div className="flex items-center justify-between gap-2 pl-[112px]">
                {urls.length < 100 && (
                  <button
                    type="button"
                    onClick={() => setAddingTo({ itemIndex: i })}
                    className="rounded-md border border-dashed border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/40"
                    title={
                      urls.length <= 1
                        ? 'Add more URLs — Poof will rotate between them so all your accounts grow evenly'
                        : 'Add more URLs to the rotation pool'
                    }
                  >
                    + Add URLs
                  </button>
                )}
                {multi && (
                  <span className="text-[11px] text-[var(--color-muted-foreground)]">
                    Rotating across {urls.length} URLs
                  </span>
                )}
              </div>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() =>
            writeItems([
              ...items.map((x) => ({ platform: x.platform, urls: getItemUrls(x) })),
              { platform: 'instagram', urls: ['https://'] },
            ])
          }
          className="rounded-md border border-dashed border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/40"
        >
          + Add profile
        </button>
      </div>

      <AddRotatorUrlsDialog
        open={addingTo !== null}
        platform={activePlatform}
        existing={activePool}
        onClose={() => setAddingTo(null)}
        onAdd={(toAdd) => {
          if (!addingTo) return;
          const cur = getItemUrls(items[addingTo.itemIndex] || { platform: 'instagram' });
          // If the primary URL is still the empty placeholder, replace
          // it with the first new URL so the row's icon points
          // somewhere meaningful instead of "https://". Otherwise
          // append.
          let next = cur.slice();
          if (next.length === 1 && (next[0] === 'https://' || next[0] === '')) {
            next = [toAdd[0]!, ...toAdd.slice(1)];
          } else {
            next = [...next, ...toAdd];
          }
          updateItemUrls(addingTo.itemIndex, next.slice(0, 100));
        }}
      />
    </>
  );
}

function ImageInspector({ props, onChange }: InspectorProps) {
  const widthPct = typeof props.widthPct === 'number' ? props.widthPct : 100;
  const aspectRatio = (props.aspectRatio as string) || 'auto';
  const align = (props.align as string) || 'center';

  return (
    <>
      <ImageUploadField
        label="Image URL"
        value={(props.src as string) || ''}
        onChange={(v) => onChange({ ...props, src: v })}
      />
      <TextField
        label="Alt text"
        value={(props.alt as string) || ''}
        onChange={(v) => onChange({ ...props, alt: v })}
      />
      <TextField
        label="Caption"
        value={(props.caption as string) || ''}
        onChange={(v) => onChange({ ...props, caption: v })}
      />

      {/* Width — slider + quick presets */}
      <div>
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Width
          </span>
          <span className="font-mono text-xs text-[var(--color-muted-foreground)]">
            {widthPct}%
          </span>
        </div>
        <input
          type="range"
          min={25}
          max={100}
          step={5}
          value={widthPct}
          onChange={(e) => onChange({ ...props, widthPct: Number(e.target.value) })}
          className="mt-2 w-full"
        />
        <div className="mt-2 flex gap-1.5">
          {[33, 50, 75, 100].map((p) => (
            <button
              type="button"
              key={p}
              onClick={() => onChange({ ...props, widthPct: p })}
              className={`flex-1 rounded-md border px-2 py-1 text-xs transition ${
                widthPct === p
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                  : 'border-[var(--color-border)] hover:bg-[var(--color-muted)]/40'
              }`}
            >
              {p}%
            </button>
          ))}
        </div>
      </div>

      {/* Aspect ratio */}
      <SelectField
        label="Aspect ratio"
        value={aspectRatio}
        options={[
          { value: 'auto', label: 'Auto (use the photo as-is)' },
          { value: '1/1', label: 'Square (1:1)' },
          { value: '3/4', label: 'Portrait (3:4)' },
          { value: '9/16', label: 'Tall (9:16) — IG story' },
          { value: '4/3', label: 'Landscape (4:3)' },
          { value: '16/9', label: 'Widescreen (16:9)' },
          { value: '3/1', label: 'Wide banner (3:1)' },
        ]}
        onChange={(v) => onChange({ ...props, aspectRatio: v })}
      />
      {aspectRatio !== 'auto' && (
        <p className="-mt-2 text-[11px] text-[var(--color-muted-foreground)]">
          Photo is cropped to fit this shape — adjust if your subject gets cut off.
        </p>
      )}

      {/* Alignment — only meaningful at <100% width */}
      <SelectField
        label="Alignment"
        value={align}
        options={[
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
        ]}
        onChange={(v) => onChange({ ...props, align: v })}
      />

      <SelectField
        label="Corners"
        value={(props.shape as string) || 'rounded'}
        options={[
          { value: 'rounded', label: 'Rounded' },
          { value: 'square', label: 'Square (sharp)' },
        ]}
        onChange={(v) => onChange({ ...props, shape: v })}
      />
    </>
  );
}

function DividerInspector({ props, onChange }: InspectorProps) {
  return (
    <>
      <NumberField
        label="Thickness (px)"
        value={(props.thickness as number) ?? 1}
        min={0}
        max={4}
        onChange={(v) => onChange({ ...props, thickness: v })}
      />
      <NumberField
        label="Spacing (px)"
        value={(props.spacing as number) ?? 8}
        min={0}
        max={64}
        onChange={(v) => onChange({ ...props, spacing: v })}
      />
    </>
  );
}

function CountdownInspector({ props, onChange }: InspectorProps) {
  // Back-compat: legacy rows have no `mode` field. Infer based on which
  // value is present (targetIso → datetime, else default to duration —
  // which is the new default for fresh rows anyway).
  const mode: 'duration' | 'datetime' =
    props.mode === 'datetime' ? 'datetime' : props.mode === 'duration' ? 'duration' : 'duration';
  const durationSeconds = typeof props.durationSeconds === 'number' ? props.durationSeconds : 300;

  // Convert ISO ↔ datetime-local — the <input type=datetime-local> wants
  // a local string without seconds, so we slice + normalize.
  const iso = (props.targetIso as string) || '';
  const localD = iso ? new Date(iso) : null;
  const dtLocal =
    localD && !Number.isNaN(localD.getTime())
      ? new Date(localD.getTime() - localD.getTimezoneOffset() * 60_000)
          .toISOString()
          .slice(0, 16)
      : '';

  // Decompose duration into hours/minutes/seconds for the picker.
  const hh = Math.floor(durationSeconds / 3600);
  const mm = Math.floor((durationSeconds % 3600) / 60);
  const ss = durationSeconds % 60;

  function setDuration(h: number, m: number, s: number) {
    const total = Math.max(1, h * 3600 + m * 60 + s);
    onChange({ ...props, durationSeconds: total });
  }

  return (
    <>
      <div>
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Countdown type
        </span>
        <div className="mt-2 space-y-2">
          <label className="flex cursor-pointer items-start gap-2 rounded-md p-2 hover:bg-[var(--color-muted)]/40">
            <input
              type="radio"
              checked={mode === 'duration'}
              onChange={() => onChange({ ...props, mode: 'duration' })}
              className="mt-1"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">Urgency timer</div>
              <div className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
                Fresh countdown of the duration below each time someone visits. Great for
                conversion pressure (&ldquo;only 2:30 left!&rdquo;).
              </div>
            </div>
          </label>
          <label className="flex cursor-pointer items-start gap-2 rounded-md p-2 hover:bg-[var(--color-muted)]/40">
            <input
              type="radio"
              checked={mode === 'datetime'}
              onChange={() => onChange({ ...props, mode: 'datetime' })}
              className="mt-1"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">Real event countdown</div>
              <div className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
                Counts down to a fixed date and time. Use for genuine launches / drops.
              </div>
            </div>
          </label>
        </div>
      </div>

      <TextField
        label="Headline"
        value={(props.label as string) || ''}
        onChange={(v) => onChange({ ...props, label: v })}
        placeholder={mode === 'duration' ? 'Offer ends in' : 'Launch ends in'}
      />

      {mode === 'duration' ? (
        <div>
          <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Duration
          </span>
          <div className="mt-1 grid grid-cols-3 gap-2">
            <DurationField
              label="Hours"
              value={hh}
              max={167}
              onChange={(v) => setDuration(v, mm, ss)}
            />
            <DurationField
              label="Minutes"
              value={mm}
              max={59}
              onChange={(v) => setDuration(hh, v, ss)}
            />
            <DurationField
              label="Seconds"
              value={ss}
              max={59}
              onChange={(v) => setDuration(hh, mm, v)}
            />
          </div>
          <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
            Total: {formatDuration(durationSeconds)}. Each new visitor sees this full
            countdown when they open your page.
          </p>
        </div>
      ) : (
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Target date &amp; time (your local time)
          </span>
          <input
            type="datetime-local"
            value={dtLocal}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              const d = new Date(v);
              if (!Number.isNaN(d.getTime())) onChange({ ...props, targetIso: d.toISOString() });
            }}
            className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
          />
        </label>
      )}

      <TextField
        label="Text shown after it ends"
        value={(props.expiredText as string) || ''}
        onChange={(v) => onChange({ ...props, expiredText: v })}
        placeholder="It's live!"
      />
      <AlignField
        value={(props.align as string) || 'center'}
        onChange={(v) => onChange({ ...props, align: v })}
      />
    </>
  );
}

function StockPillInspector({ props, onChange }: InspectorProps) {
  const startCount = typeof props.startCount === 'number' ? props.startCount : 10;
  const endCount = typeof props.endCount === 'number' ? props.endCount : 1;
  const durationSeconds = typeof props.durationSeconds === 'number' ? props.durationSeconds : 600;
  const template = (props.template as string) || '🔥 Only {n} spots left at this price';
  const variant = (props.variant as string) || 'warning';

  const hh = Math.floor(durationSeconds / 3600);
  const mm = Math.floor((durationSeconds % 3600) / 60);
  const ss = durationSeconds % 60;

  function setDuration(h: number, m: number, s: number) {
    onChange({ ...props, durationSeconds: Math.max(1, h * 3600 + m * 60 + s) });
  }

  // Decrement cadence preview — how long between each "spot disappears."
  const steps = Math.max(1, startCount - endCount);
  const secsPerStep = Math.floor(durationSeconds / steps);

  return (
    <>
      <div>
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Template
        </span>
        <input
          type="text"
          value={template}
          onChange={(e) => onChange({ ...props, template: e.target.value })}
          placeholder="🔥 Only {n} spots left"
          className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
        />
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          Use <code className="rounded bg-[var(--color-muted)] px-1 font-mono">{'{n}'}</code> where you want the
          running number to appear.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Starts at
          </span>
          <input
            type="number"
            min={1}
            max={9999}
            value={startCount}
            onChange={(e) => {
              const n = Math.max(1, Math.min(9999, Number(e.target.value) || 0));
              onChange({ ...props, startCount: n, endCount: Math.min(endCount, n - 1) });
            }}
            className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-center text-sm outline-none transition focus:border-[var(--color-ring)]"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Stops at
          </span>
          <input
            type="number"
            min={0}
            max={startCount - 1}
            value={endCount}
            onChange={(e) => {
              const n = Math.max(0, Math.min(startCount - 1, Number(e.target.value) || 0));
              onChange({ ...props, endCount: n });
            }}
            className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-center text-sm outline-none transition focus:border-[var(--color-ring)]"
          />
        </label>
      </div>

      <div>
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Decrements over
        </span>
        <div className="mt-1 grid grid-cols-3 gap-2">
          <DurationField label="Hours" value={hh} max={167} onChange={(v) => setDuration(v, mm, ss)} />
          <DurationField label="Minutes" value={mm} max={59} onChange={(v) => setDuration(hh, v, ss)} />
          <DurationField label="Seconds" value={ss} max={59} onChange={(v) => setDuration(hh, mm, v)} />
        </div>
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          {steps} step{steps === 1 ? '' : 's'} · about {formatDuration(secsPerStep)} between each spot
          disappearing. Resets to {startCount} for every new visitor.
        </p>
      </div>

      <SelectField
        label="Style"
        value={variant}
        options={[
          { value: 'warning', label: 'Warning (amber)' },
          { value: 'danger', label: 'Danger (red)' },
          { value: 'minimal', label: 'Minimal (neutral)' },
        ]}
        onChange={(v) => onChange({ ...props, variant: v })}
      />
      <AlignField
        value={(props.align as string) || 'center'}
        onChange={(v) => onChange({ ...props, align: v })}
      />
    </>
  );
}

function DurationField({
  label,
  value,
  max,
  onChange,
}: {
  label: string;
  value: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <input
        type="number"
        value={value}
        min={0}
        max={max}
        onChange={(e) => {
          const n = Math.max(0, Math.min(max, Number(e.target.value) || 0));
          onChange(n);
        }}
        className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-center text-sm outline-none transition focus:border-[var(--color-ring)]"
      />
    </label>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function VideoEmbedInspector({ props, onChange }: InspectorProps) {
  return (
    <>
      <TextField
        label="Video URL"
        value={(props.url as string) || ''}
        onChange={(v) => onChange({ ...props, url: v })}
        placeholder="https://youtu.be/… or tiktok.com/@… or instagram.com/reel/…"
      />
      <p className="text-xs text-[var(--color-muted-foreground)]">
        Paste any YouTube, Vimeo, TikTok, or Instagram Reel URL. We detect the provider
        automatically.
      </p>
      <CheckboxField
        label="Portrait (9:16) — best for Reels & TikTok"
        checked={!!props.portrait}
        onChange={(v) => onChange({ ...props, portrait: v })}
      />
    </>
  );
}

function StickyCtaInspector({ props, onChange }: InspectorProps) {
  return (
    <>
      <TextField
        label="Button label"
        value={(props.label as string) || ''}
        onChange={(v) => onChange({ ...props, label: v })}
      />
      <TextField
        label="URL"
        value={(props.url as string) || ''}
        onChange={(v) => onChange({ ...props, url: v })}
        placeholder="https://"
      />
      <SelectField
        label="Position"
        value={(props.position as string) || 'bottom'}
        options={[
          { value: 'bottom', label: 'Bottom of screen' },
          { value: 'top', label: 'Top of screen' },
        ]}
        onChange={(v) => onChange({ ...props, position: v })}
      />
      <CheckboxField
        label="Fade out near the end of the page"
        checked={props.hideAtEnd !== false}
        onChange={(v) => onChange({ ...props, hideAtEnd: v })}
      />
    </>
  );
}

function InstagramBioInspector({ props, onChange }: InspectorProps) {
  const avatarSrc = (props.avatar as string) || '';
  const avatarCropX = typeof props.avatarCropX === 'number' ? props.avatarCropX : 50;
  const avatarCropY = typeof props.avatarCropY === 'number' ? props.avatarCropY : 50;
  return (
    <>
      <p className="text-xs text-[var(--color-muted-foreground)]">
        Builds the Instagram profile-header look at the top of your page. Fill it in by
        hand (Instagram's API doesn't let us pull it for you, especially for 18+ /
        restricted accounts — but you'll have it set up in 30 seconds).
      </p>

      <div className="space-y-3 rounded-md border border-[var(--color-border)] p-3">
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Identity
        </span>
        <ImageUploadField
          label="Avatar"
          value={avatarSrc}
          onChange={(v) =>
            onChange({
              ...props,
              avatar: v,
              // Reset position when the photo changes — old coords won't
              // make sense on a different image.
              avatarCropX: 50,
              avatarCropY: 50,
            })
          }
        />
        {avatarSrc && (
          <AvatarCropper
            src={avatarSrc}
            x={avatarCropX}
            y={avatarCropY}
            onChange={(x, y) => onChange({ ...props, avatarCropX: x, avatarCropY: y })}
            onReset={() =>
              onChange({ ...props, avatarCropX: 50, avatarCropY: 50 })
            }
          />
        )}
        <SelectField
          label="Avatar size"
          value={(props.avatarSize as string) || 'md'}
          options={[
            { value: 'sm', label: 'Small (72px)' },
            { value: 'md', label: 'Medium (86px)' },
            { value: 'lg', label: 'Large (108px)' },
          ]}
          onChange={(v) => onChange({ ...props, avatarSize: v })}
        />
        <TextField
          label="Initials (fallback)"
          value={(props.avatarInitials as string) || ''}
          maxLength={3}
          onChange={(v) => onChange({ ...props, avatarInitials: v })}
        />
        <CheckboxField
          label="Show IG story ring (colorful gradient)"
          checked={props.storyRing !== false}
          onChange={(v) => onChange({ ...props, storyRing: v })}
        />
      </div>

      <div className="space-y-3 rounded-md border border-[var(--color-border)] p-3">
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Username
        </span>
        <TextField
          label="Handle"
          value={(props.username as string) || ''}
          onChange={(v) => onChange({ ...props, username: v.replace(/^@/, '') })}
          placeholder="yourhandle (we add the @)"
        />
        <CheckboxField
          label="Show blue verified checkmark (inline)"
          checked={!!props.verified}
          onChange={(v) => onChange({ ...props, verified: v })}
        />
        <TextField
          label="Display name"
          value={(props.displayName as string) || ''}
          onChange={(v) => onChange({ ...props, displayName: v })}
          placeholder="Your Name"
        />
      </div>

      <div className="rounded-md border border-[var(--color-border)] p-3">
        <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Stats (any free-text format)
        </span>
        <div className="grid grid-cols-3 gap-2">
          <DurationLikeField
            label="Posts"
            value={(props.posts as string) || ''}
            onChange={(v) => onChange({ ...props, posts: v })}
          />
          <DurationLikeField
            label="Followers"
            value={(props.followers as string) || ''}
            onChange={(v) => onChange({ ...props, followers: v })}
          />
          <DurationLikeField
            label="Following"
            value={(props.following as string) || ''}
            onChange={(v) => onChange({ ...props, following: v })}
          />
        </div>
        <p className="mt-2 text-[11px] text-[var(--color-muted-foreground)]">
          Try things like &ldquo;197&rdquo;, &ldquo;12.4K&rdquo;, or &ldquo;1.2M&rdquo;.
        </p>
      </div>

      <div className="space-y-3 rounded-md border border-[var(--color-border)] p-3">
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Bio
        </span>
        <TextField
          label="Category"
          value={(props.category as string) || ''}
          onChange={(v) => onChange({ ...props, category: v })}
          placeholder="Creator · Public figure · Brand"
        />
        <TextAreaField
          label="Bio body"
          value={(props.bio as string) || ''}
          onChange={(v) => onChange({ ...props, bio: v })}
        />
      </div>
    </>
  );
}

// Reuses TextField's styling but keeps the import surface clean. Same
// underlying primitive — separate name only for inline grouping clarity.
function DurationLikeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-center text-sm outline-none transition focus:border-[var(--color-ring)]"
      />
    </label>
  );
}

function CarouselInspector({ props, onChange }: InspectorProps) {
  const slides =
    (props.slides as Array<{ src: string; alt: string; caption?: string; url?: string }>) || [];
  const aspectRatio = (props.aspectRatio as string) || '1/1';
  const shape = (props.shape as string) || 'rounded';
  const showCounter = !!props.showCounter;

  function updateSlides(
    next: Array<{ src: string; alt: string; caption?: string; url?: string }>,
  ) {
    onChange({ ...props, slides: next });
  }

  return (
    <>
      <p className="text-xs text-[var(--color-muted-foreground)]">
        Visitors swipe horizontally between slides. The next image always peeks in from the
        edge so they know to swipe — no dots needed.
      </p>

      <SelectField
        label="Aspect ratio"
        value={aspectRatio}
        options={[
          { value: '1/1', label: 'Square (1:1)' },
          { value: '3/4', label: 'Portrait (3:4)' },
          { value: '9/16', label: 'Tall (9:16)' },
          { value: '4/3', label: 'Landscape (4:3)' },
          { value: '16/9', label: 'Widescreen (16:9)' },
          { value: 'auto', label: 'Auto (square fallback)' },
        ]}
        onChange={(v) => onChange({ ...props, aspectRatio: v })}
      />
      <SelectField
        label="Corners"
        value={shape}
        options={[
          { value: 'rounded', label: 'Rounded' },
          { value: 'square', label: 'Square (sharp)' },
        ]}
        onChange={(v) => onChange({ ...props, shape: v })}
      />
      <CheckboxField
        label='Show "1 / 5" counter overlay on each slide'
        checked={showCounter}
        onChange={(v) => onChange({ ...props, showCounter: v })}
      />

      <div className="space-y-3">
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Slides
        </span>
        {slides.map((slide, i) => (
          <div key={i} className="rounded-md border border-[var(--color-border)] p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
                Slide {i + 1}
              </span>
              <div className="flex items-center gap-1 text-xs">
                <button
                  type="button"
                  disabled={i === 0}
                  onClick={() => {
                    if (i === 0) return;
                    const next = slides.slice();
                    [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
                    updateSlides(next);
                  }}
                  className="rounded p-1 hover:bg-[var(--color-muted)] disabled:opacity-30"
                  aria-label="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  disabled={i === slides.length - 1}
                  onClick={() => {
                    if (i === slides.length - 1) return;
                    const next = slides.slice();
                    [next[i], next[i + 1]] = [next[i + 1]!, next[i]!];
                    updateSlides(next);
                  }}
                  className="rounded p-1 hover:bg-[var(--color-muted)] disabled:opacity-30"
                  aria-label="Move down"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => updateSlides(slides.filter((_, j) => j !== i))}
                  className="rounded px-2 text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
                  aria-label="Remove slide"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="mt-2 space-y-2">
              <ImageUploadField
                label="Image"
                value={slide.src}
                onChange={(v) => {
                  const next = slides.slice();
                  next[i] = { ...slide, src: v };
                  updateSlides(next);
                }}
              />
              <input
                type="text"
                value={slide.caption || ''}
                onChange={(e) => {
                  const next = slides.slice();
                  next[i] = { ...slide, caption: e.target.value };
                  updateSlides(next);
                }}
                placeholder="Caption (optional, appears at the bottom)"
                className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
              />
              <input
                type="text"
                value={slide.url || ''}
                onChange={(e) => {
                  const next = slides.slice();
                  next[i] = { ...slide, url: e.target.value };
                  updateSlides(next);
                }}
                placeholder="Link URL (optional — tapping the slide opens this)"
                className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
              />
              <input
                type="text"
                value={slide.alt}
                onChange={(e) => {
                  const next = slides.slice();
                  next[i] = { ...slide, alt: e.target.value };
                  updateSlides(next);
                }}
                placeholder="Alt text (accessibility)"
                className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
              />
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => updateSlides([...slides, { src: '', alt: '', caption: '', url: '' }])}
        className="rounded-md border border-dashed border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/40"
      >
        + Add slide
      </button>
    </>
  );
}

function LiveStatusInspector({ props, onChange }: InspectorProps) {
  const variant = (props.variant as string) || 'online';
  const PRESETS: Record<string, { label: string; defaultText: string; previewBg: string; previewDot: string; previewText: string }> = {
    online: {
      label: '🟢 Online',
      defaultText: 'Online now',
      previewBg: 'rgba(34,197,94,0.12)',
      previewDot: '#22c55e',
      previewText: '#16a34a',
    },
    live: {
      label: '🔴 LIVE',
      defaultText: 'LIVE NOW',
      previewBg: 'rgba(239,68,68,0.12)',
      previewDot: '#ef4444',
      previewText: '#dc2626',
    },
    away: {
      label: '🟡 Away',
      defaultText: 'Be right back',
      previewBg: 'rgba(245,158,11,0.12)',
      previewDot: '#f59e0b',
      previewText: '#b45309',
    },
    busy: {
      label: '🟠 Busy',
      defaultText: 'Busy until 5pm',
      previewBg: 'rgba(251,146,60,0.12)',
      previewDot: '#fb923c',
      previewText: '#c2410c',
    },
  };
  const preset = PRESETS[variant] ?? PRESETS.online!;

  return (
    <>
      <div>
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Variant
        </span>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {Object.entries(PRESETS).map(([key, p]) => (
            <button
              type="button"
              key={key}
              onClick={() =>
                onChange({
                  ...props,
                  variant: key,
                  // Replace label only when switching to a still-default value.
                  // Otherwise the customer's edit is preserved.
                  label: PRESETS[(props.variant as string) || 'online']?.defaultText === props.label
                    ? p.defaultText
                    : (props.label as string),
                })
              }
              className={`rounded-md border px-2 py-1.5 text-xs transition ${
                variant === key
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                  : 'border-[var(--color-border)] hover:bg-[var(--color-muted)]/40'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <TextField
        label="Label"
        value={(props.label as string) || ''}
        onChange={(v) => onChange({ ...props, label: v })}
        placeholder={preset.defaultText}
      />

      <AlignField
        value={(props.align as string) || 'center'}
        onChange={(v) => onChange({ ...props, align: v })}
      />

      {/* Mini preview so the customer can see the styled pill without
          having to glance at the canvas. */}
      <div>
        <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Preview
        </span>
        <div className="mt-2 flex justify-center rounded-md border border-[var(--color-border)] p-3">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 14px',
              background: preset.previewBg,
              color: preset.previewText,
              borderRadius: 9999,
              fontSize: 13,
              fontWeight: 600,
              textTransform: variant === 'live' ? 'uppercase' : 'none',
              letterSpacing: variant === 'live' ? '0.06em' : 0,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                background: preset.previewDot,
                borderRadius: 9999,
              }}
            />
            {(props.label as string) || preset.defaultText}
          </span>
        </div>
      </div>
    </>
  );
}

function LinkListInspector({ props, onChange }: InspectorProps) {
  const items =
    (props.items as Array<{ label: string; url: string; thumbnail?: string; caption?: string }>) ||
    [];
  const updateItems = (
    next: Array<{ label: string; url: string; thumbnail?: string; caption?: string }>,
  ) => onChange({ ...props, items: next });
  return (
    <>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="rounded-md border border-[var(--color-border)] p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
                Row {i + 1}
              </span>
              <button
                onClick={() => updateItems(items.filter((_, j) => j !== i))}
                className="rounded px-2 text-sm text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
                aria-label="Remove row"
              >
                ✕
              </button>
            </div>
            <div className="mt-2 space-y-2">
              <input
                type="text"
                value={it.label}
                onChange={(e) => {
                  const next = items.slice();
                  next[i] = { ...it, label: e.target.value };
                  updateItems(next);
                }}
                placeholder="Label"
                className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
              />
              <input
                type="text"
                value={it.url}
                onChange={(e) => {
                  const next = items.slice();
                  next[i] = { ...it, url: e.target.value };
                  updateItems(next);
                }}
                placeholder="https://…"
                className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
              />
              <input
                type="text"
                value={it.caption || ''}
                onChange={(e) => {
                  const next = items.slice();
                  next[i] = { ...it, caption: e.target.value };
                  updateItems(next);
                }}
                placeholder="Caption (optional)"
                className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
              />
              <ImageUploadField
                label="Thumbnail (optional)"
                value={it.thumbnail || ''}
                onChange={(v) => {
                  const next = items.slice();
                  next[i] = { ...it, thumbnail: v };
                  updateItems(next);
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={() => updateItems([...items, { label: '', url: 'https://' }])}
        className="mt-3 rounded-md border border-dashed border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/40"
      >
        + Add row
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------
// Tiny form-control primitives
// ---------------------------------------------------------------------------

function TextField({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
      />
    </label>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
      />
    </label>
  );
}

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded"
      />
      {label}
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
      />
    </label>
  );
}

function AlignField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <SelectField
      label="Align"
      value={value}
      options={[
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ]}
      onChange={onChange}
    />
  );
}

function ColorPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const hex = `#${value.replace(/^#/, '')}`;
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="color"
          value={hex}
          onChange={(e) => onChange(e.target.value.replace(/^#/, ''))}
          className="h-9 w-9 cursor-pointer rounded border border-[var(--color-border)] bg-transparent"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/^#/, ''))}
          maxLength={6}
          className="min-w-0 flex-1 rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 font-mono text-sm outline-none transition focus:border-[var(--color-ring)]"
        />
      </div>
    </label>
  );
}

function ImageUploadField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErr(null);
    try {
      const res = await uploadApi.image(file);
      onChange(res.url);
    } catch (ex) {
      setErr(uploadErrorFor(ex));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div>
      <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <div className="mt-1 flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="min-w-0 flex-1 rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs transition hover:bg-[var(--color-muted)] disabled:opacity-50"
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
          onChange={onPick}
          className="hidden"
        />
      </div>
      {err && <p className="mt-1 text-xs text-[var(--color-destructive)]">{err}</p>}
      {value && (
        <div className="mt-2">
          <img
            src={value}
            alt=""
            className="h-20 w-20 rounded-md border border-[var(--color-border)] object-cover"
          />
        </div>
      )}
    </div>
  );
}

/** Translate upload API error codes into human copy. */
function uploadErrorFor(ex: unknown): string {
  const code = ex instanceof ApiError ? ex.code : '';
  switch (code) {
    case 'file_too_large':
      return 'That image is too large, even after we shrank it. Try a smaller or lower-resolution one.';
    case 'mime_not_allowed':
      return 'Only PNG, JPEG, WebP, GIF, or AVIF images are supported.';
    case 'file_missing':
      return 'No file picked — try again.';
    case 'expected_multipart':
    case 'invalid_form':
      return "Something went wrong on the upload. Refresh and try again.";
    default:
      return (ex as Error)?.message || 'Upload failed.';
  }
}
